<ng-template #currentPlanCard>
  <div class="sub-card">
    <div class="sub-card-head">
      <div>
        <p class="sub-card-eyebrow">Current Plan</p>
        <p class="sub-card-desc">Your active plan and everything it includes.</p>
      </div>
    </div>
    <div class="custom-line-bottom my-3"></div>

    <p class="sub-plan-name">{{ currentSubscriptionPlan?.name }}</p>
    <div class="sub-plan-price-row">
      <span class="sub-plan-price">{{ currentPriceLabel.amount }}</span>
      @if (currentPriceLabel.suffix === '/order') {
        <span class="sub-plan-price-suffix">/order</span>
      } @else if (currentPriceLabel.suffix) {
        <span class="sub-plan-price-suffix"
          >/{{ currentSubscriptionPlan?.currentPrice?.billingInterval }}</span
        >
      }
    </div>
    @if (currentPriceLabel.detail) {
      <p class="sub-plan-price-detail">
        {{ currentPriceLabel.detail
        }}{{
          currentPriceLabel.suffix === '/order'
            ? ' · billed after each month from completed orders'
            : ''
        }}
      </p>
    }
    @if (usage) {
      <div class="sub-usage">
        <div class="sub-usage-row">
          <span class="sub-usage-label">Orders this month</span>
          <span class="sub-usage-value">{{ usage.orders | number }}</span>
        </div>
        <div class="sub-usage-row">
          <span class="sub-usage-label">Estimated charge</span>
          <span class="sub-usage-value"
            >{{ usageEstimateLabel }}
            @if (usage.capped) {
              <span class="sub-usage-note"> · flat price reached</span>
            }
          </span>
        </div>
        @if (usage.addOnsEstimatedAmountCents) {
          <div class="sub-usage-row">
            <span class="sub-usage-label">Add-ons so far</span>
            <span class="sub-usage-value">{{ usageAddOnsLabel }}</span>
          </div>
        }
        <p class="sub-usage-hint">
          @if (usage.promoEndsAt) {
            Orders before {{ convertSecondsToRestaurantTime(usage.promoEndsAt) }} are free
            (promotion).
          }
          Billed on {{ convertSecondsToRestaurantTime(usage.periodEnd) }} from completed orders.
        </p>
      </div>
    }
    <p class="sub-plan-desc">{{ currentSubscriptionPlan?.description }}</p>

    <div class="sub-chip-row">
      <div class="sub-chip">
        <span class="sub-chip-label">Billing cycle</span>
        <span class="sub-chip-value">{{
          currentSubscriptionPlan?.currentPrice?.billingInterval
        }}</span>
      </div>
      <div class="sub-chip">
        <span class="sub-chip-label">Last Renewal</span>
        <span class="sub-chip-value">{{
          convertSecondsToRestaurantTime(currentSubscription?.currentPeriodStart)
        }}</span>
      </div>
      <div class="sub-chip">
        <span class="sub-chip-label">Next Renewal</span>
        <span class="sub-chip-value">{{
          convertSecondsToRestaurantTime(currentSubscription?.currentPeriodEnd)
        }}</span>
      </div>
    </div>

    <button type="button" class="sub-btn sub-btn--dark sub-btn--block" (click)="openManagePlan()">
      Manage Plans
    </button>
  </div>
</ng-template>

<ng-template #addOnsCard>
  @if (displayAddon) {
    <div class="sub-card">
      <div class="sub-card-head">
        <div>
          <p class="sub-card-eyebrow">Add-ons</p>
          <p class="sub-card-desc">Your active plan and everything it includes.</p>
        </div>
        <button type="button" class="sub-card-link" (click)="setActiveTab('add-ons')">
          Manage Add-ons
        </button>
      </div>
      <div class="custom-line-bottom my-3"></div>
      @if (currentSubscriptionAddons.length > 0) {
        <div class="sub-row-list">
          @for (addOn of currentSubscriptionAddons; track addOn.name) {
            <div class="sub-row">
              <div>
                <div class="sub-row-title">{{ addOn.name }}</div>
                <div class="sub-row-sub">{{ addOn.description }}</div>
              </div>
              <div class="sub-row-right">
                <span
                  class="sub-pill"
                  [class.sub-pill--success]="addOn.status === 'success'"
                  [class.sub-pill--warning]="addOn.status === 'warning'"
                >
                  <span class="sub-pill-dot"></span>{{ addOn.statusLabel }}
                </span>
                <span class="sub-row-amount"
                  >{{ getCurrencySymbol(currency) }}{{ addOn.price.toFixed(2) }}</span
                >
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="sub-row-list text-center">
          <div class="sub-row-sub">Add required add-ons</div>
        </div>
      }
    </div>
  }
</ng-template>

<ng-template #paymentMethodsCard>
  <div class="sub-card">
    <div class="sub-card-head">
      <div>
        <p class="sub-card-eyebrow">Payment Method</p>
        <p class="sub-card-desc">The card your plan and add-ons are billed to.</p>
      </div>
      @if (currentPaymentMethod?.hasPaymentMethod && activeTab !== 'payment-method') {
        <button type="button" class="sub-card-link" (click)="setActiveTab('payment-method')">
          Change
        </button>
      }
    </div>
    <div class="custom-line-bottom my-3"></div>

    @if (currentPaymentMethod?.hasPaymentMethod) {
      <div class="pm-saved">
        <div class="pm-card-chip" aria-hidden="true">
          <i class="bx bx-credit-card"></i>
        </div>
        <div class="pm-saved-info">
          <div class="pm-saved-title">
            <span class="pm-brand">{{ currentPaymentMethod?.brand }}</span>
            •••• {{ currentPaymentMethod?.last4 }}
            <span class="sub-pill sub-pill--success"
              ><span class="sub-pill-dot"></span>Default</span
            >
          </div>
          <div class="pm-saved-sub">
            @if (currentPaymentMethod?.expMonth && currentPaymentMethod?.expYear) {
              Expires {{ currentPaymentMethod?.expMonth }}/{{ currentPaymentMethod?.expYear }}
            } @else {
              Charged at the end of each billing period
            }
            @if (currentPaymentMethod?.holderName) {
              · {{ currentPaymentMethod?.holderName }}
            }
          </div>
        </div>
        <div class="pm-saved-actions">
          <button
            type="button"
            class="sub-btn sub-btn--outline"
            (click)="addOrUpdatePaymentMethod()"
          >
            <i class="bx bx-refresh"></i> Replace card
          </button>
          <button type="button" class="sub-btn sub-btn--outline" (click)="manageBilling()">
            <i class="bx bx-cog"></i> Manage billing
          </button>
        </div>
      </div>
      <p class="pm-trust">
        <i class="bx bx-lock-alt"></i> Card details are stored securely by Stripe.
      </p>
    } @else {
      <div class="pm-empty">
        <div class="pm-empty-icon" aria-hidden="true">
          <i class="bx bx-credit-card"></i>
        </div>
        <div class="pm-empty-body">
          <p class="pm-empty-title">No payment method yet</p>
          <p class="pm-empty-text">
            @if (promoCoversBilling) {
              Nothing is charged until
              {{ convertSecondsToRestaurantTime(currentSubscription?.promoEndsAt) }}. Add a card now
              so your plan and add-ons carry on without interruption.
            } @else {
              Add a card so your plan and add-ons keep running without interruption.
            }
          </p>
          <p class="pm-trust">
            <i class="bx bx-lock-alt"></i> Secured by Stripe · we never see or store your card
            number.
          </p>
        </div>
        <button type="button" class="sub-btn sub-btn--dark" (click)="addOrUpdatePaymentMethod()">
          <i class="bx bx-plus"></i> Add payment method
        </button>
      </div>
    }
  </div>
</ng-template>

<ng-template #restaurantInfoCard>
  <div class="sub-card">
    <p class="sub-card-eyebrow">Restaurant Information</p>
    <p class="sub-card-desc">This plan is currently assigned to this restaurant.</p>
    <div class="custom-line-bottom my-3"></div>

    <div class="sub-entity-row">
      <span class="sub-entity-icon"><i class="bx bx-store"></i></span>
      <div>
        <div class="sub-entity-name">{{ currentRestaurant?.restaurant_name }}</div>
        <div class="sub-entity-sub">Restaurant</div>
      </div>
    </div>

    <div class="sub-chip-row1" style="margin-bottom: 0">
      <!-- <div class="sub-chip">
        <span class="sub-chip-label">Restaurant ID</span>
        <span class="sub-chip-value" [title]="restaurantId">{{ restaurantId }}</span>
      </div> -->
      <div class="sub-chip">
        <span class="sub-chip-label">Location</span>
        <span class="sub-chip-value" [title]="restaurantLocation">{{ restaurantLocation }}</span>
      </div>
      <div class="sub-chip">
        <span class="sub-chip-label">Member Since</span>
        <span class="sub-chip-value">{{
          convertSecondsToRestaurantTime(currentSubscription?.createdAt)
        }}</span>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #nextInvoiceCard>
  <div class="sub-card">
    <div class="sub-card-head">
      <div>
        <p class="sub-card-eyebrow">Next Invoice</p>
        <p class="sub-card-desc">Your next scheduled payment and billing breakdown.</p>
      </div>
      <button type="button" class="sub-card-link" (click)="setActiveTab('billing-history')">
        View Billing History
      </button>
    </div>
    <div class="custom-line-bottom my-3"></div>

    @if (nextInvoicePreview) {
      <div class="sub-invoice-highlight">
        <span class="sub-invoice-icon"><i class="bx bx-receipt"></i></span>
        <div class="sub-invoice-main">
          <span class="sub-pill sub-pill--warning"><span class="sub-pill-dot"></span>Upcoming</span>
          <span class="sub-invoice-date">{{
            convertSecondsToRestaurantTime(nextInvoicePreview.periodEnd)
          }}</span>
          <span class="sub-invoice-sep"></span>
          <span class="sub-invoice-amount"
            >{{ getCurrencySymbol(currency)
            }}{{ (nextInvoicePreview.amountDueCents / 100).toFixed(2) }}</span
          >
        </div>
        <button type="button" class="sub-btn sub-btn--outline" (click)="viewNextInvoice()">
          View Invoice
        </button>
      </div>
      @if (nextInvoiceAddOnsCents > 0) {
        <p class="sub-usage-hint">
          Includes {{ getCurrencySymbol(currency) }}{{ (nextInvoiceAddOnsCents / 100).toFixed(2) }}
          for add-ons so far, billed for the days you use them.
        </p>
      }
    }
    @if (recentInvoices.length > 0) {
      <p class="sub-invoice-freq">{{ currentSubscriptionPlan?.currentPrice?.billingInterval }}</p>

      <p class="sub-subhead">Past Invoices</p>
      <div class="sub-row-list">
        @for (invoice of recentInvoices; track invoice.invoiceNumber) {
          <div class="sub-row">
            <div>
              <div class="sub-row-title">
                {{ invoice.invoiceNumber }}
                @if (invoice.items.length > 0) {
                  @for (item of invoice.items; track $index) {
                    <span
                      class="bh-item-type"
                      [class.bh-item-type--addon]="item.type === 'ADDON'"
                      >{{ item.type }}</span
                    >
                  }
                }
              </div>
              <div class="sub-row-sub">{{ invoice.billingPeriod }}</div>
            </div>
            <div class="sub-row-right">
              <span class="sub-row-amount"
                >{{ getCurrencySymbol(currency) }}{{ invoice.amount.toFixed(2) }}</span
              >
              <span
                class="sub-pill"
                [class.sub-pill--success]="invoice.status === 'paid'"
                [class.sub-pill--warning]="invoice.status !== 'paid'"
                ><span class="sub-pill-dot"></span>{{ invoice.statusLabel }}</span
              >
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="sub-row-list text-center">
        <div class="sub-row-sub">No more invoices</div>
      </div>
    }
  </div>
</ng-template>

<div class="sub-page">
  <div class="sub-header-row">
    <div>
      <h1 class="sub-title">Billing</h1>
      <p class="sub-subtitle">Manage your plans, billing, and add-ons for your restaurant.</p>
    </div>
    @if (plans && plans?.plans?.length > 0) {
      <button type="button" class="sub-btn sub-btn--dark" (click)="openManagePlan()">
        Manage Plans
      </button>
    }
  </div>
  <div class="custom-line-bottom my-3"></div>

  @if (!subscriptionLoaded) {
    <div
      class="d-flex flex-column align-items-center justify-content-center common-loading-container"
    >
      <div class="w-25 text-center">
        <img class="common-loading" src="assets/menu_loading.gif" alt="common loading" />
        <p class="common-loading-text">Loading billing details, please wait...</p>
      </div>
    </div>
  } @else if (subscriptionNotFound) {
    <div class="sub-empty-state">
      <div class="sub-empty-icon"><i class="bx bx-receipt"></i></div>
      <p class="sub-empty-title">No subscription found</p>
      <p class="sub-empty-desc">
        This restaurant doesn't have a billing subscription set up yet. Set up a plan to start
        managing add-ons, invoices and payment methods.
      </p>
    </div>
  } @else {
    @if (isLimitedAccess) {
      <div class="sub-alert-banner sub-alert-banner--warning">
        <span class="sub-alert-icon"><i class="bx bx-info-circle"></i></span>
        <div class="sub-alert-text">
          <div class="sub-alert-title">You can change your plan after activation</div>
          <div class="sub-alert-desc">
            Connect your hardware to get started. Once it's connected, our support team will
            activate your restaurant within 1–2 working days, and you can then change your plan.
          </div>
        </div>
        <a class="sub-btn sub-btn--dark text-decoration-none" routerLink="/main/hardware"
          >Connect Hardware</a
        >
      </div>
    }
    @if (hasActivePromotion && currentRestaurant?.status == 'activated') {
      <div class="sub-alert-banner sub-alert-banner--success">
        <span class="sub-alert-icon"><i class="bx bx-gift"></i></span>
        <div class="sub-alert-text">
          <div class="sub-alert-title">
            {{ currentSubscription?.activePromotionName || 'Promotional pricing' }} active
          </div>
          <div class="sub-alert-desc">
            @if (currentSubscription?.activePromotion?.durationMonths) {
              Your plan is free for
              {{ currentSubscription?.activePromotion?.durationMonths }}
              month{{ currentSubscription?.activePromotion?.durationMonths === 1 ? '' : 's' }},
              until {{ convertSecondsToRestaurantTime(currentSubscription?.promoEndsAt) }}.
            } @else {
              Your plan is free until
              {{ convertSecondsToRestaurantTime(currentSubscription?.promoEndsAt) }}.
            }
            Billing resumes automatically after that — no action needed from you.
          </div>
        </div>
      </div>
    }
    @if (
      currentPaymentMethod &&
      !currentPaymentMethod?.hasPaymentMethod &&
      currentRestaurant?.status == 'activated' &&
      (currentSubscriptionPlan?.name | lowercase) != 'free'
    ) {
      <div class="sub-alert-banner sub-alert-banner--info">
        <span class="sub-alert-icon"><i class="bx bx-info-circle"></i></span>
        <div class="sub-alert-text">
          <div class="sub-alert-title">Your payment method is not configured</div>
          <div class="sub-alert-desc">
            Add a payment method to keep your subscription and add-ons active.
          </div>
        </div>
        <button type="button" class="sub-btn sub-btn--dark" (click)="addOrUpdatePaymentMethod()">
          Add Payment Method
        </button>
      </div>
    }
    @if (pendingPlan && !isCancellationScheduled && currentRestaurant?.status == 'activated') {
      <div class="sub-alert-banner sub-alert-banner--warning">
        <span class="sub-alert-icon"><i class="bx bx-calendar"></i></span>
        <div class="sub-alert-text">
          <div class="sub-alert-title">Switching to {{ pendingPlan.name }}</div>
          <div class="sub-alert-desc">
            You'll stay on {{ currentSubscriptionPlan?.name }} until
            {{ convertSecondsToRestaurantTime(currentSubscription?.pendingEffectiveAt) }}, then move
            to {{ pendingPlan.name }}.
          </div>
        </div>
        <button
          type="button"
          class="sub-btn sub-btn--dark"
          [disabled]="cancellingPendingPlan"
          (click)="cancelPendingPlanChange()"
        >
          Keep {{ currentSubscriptionPlan?.name }}
        </button>
      </div>
    }
    @if (isCancellationScheduled && currentRestaurant?.status == 'activated') {
      <div class="sub-alert-banner sub-alert-banner--warning">
        <span class="sub-alert-icon"><i class="bx bx-calendar-x"></i></span>
        <div class="sub-alert-text">
          <div class="sub-alert-title">Your subscription is set to cancel</div>
          <div class="sub-alert-desc">
            You'll keep access until
            {{ convertSecondsToRestaurantTime(currentSubscription?.currentPeriodEnd) }}. Manage
            billing to keep your subscription active.
          </div>
        </div>
        <button type="button" class="sub-btn sub-btn--dark" (click)="manageBilling()">
          Manage Billing
        </button>
      </div>
    }
    @if (isPastDue && currentRestaurant?.status == 'activated') {
      <div class="sub-alert-banner sub-alert-banner--danger">
        <span class="sub-alert-icon"><i class="bx bx-error-circle"></i></span>
        <div class="sub-alert-text">
          <div class="sub-alert-title">Your last payment failed</div>
          <div class="sub-alert-desc">
            Update your payment method to keep your subscription and add-ons active.
          </div>
        </div>
        <button type="button" class="sub-btn sub-btn--dark" (click)="addOrUpdatePaymentMethod()">
          Update Payment Method
        </button>
      </div>
    }
    <div class="sub-tabs">
      <button
        type="button"
        class="sub-tab"
        [class.sub-tab--active]="activeTab === 'overview'"
        (click)="setActiveTab('overview')"
      >
        Overview
      </button>
      @if (displayAddon) {
        <button
          type="button"
          class="sub-tab"
          [class.sub-tab--active]="activeTab === 'add-ons'"
          (click)="setActiveTab('add-ons')"
        >
          Add-ons
        </button>
      }
      <button
        type="button"
        class="sub-tab"
        [class.sub-tab--active]="activeTab === 'billing-history'"
        (click)="setActiveTab('billing-history')"
      >
        Billing History
      </button>
      <button
        type="button"
        class="sub-tab"
        [class.sub-tab--active]="activeTab === 'payment-method'"
        (click)="setActiveTab('payment-method')"
      >
        Payment Method
      </button>
    </div>

    @if (activeTab === 'overview') {
      <div class="sub-grid">
        <div class="sub-col">
          <ng-container [ngTemplateOutlet]="currentPlanCard"></ng-container>
          <ng-container [ngTemplateOutlet]="addOnsCard"></ng-container>
          <ng-container [ngTemplateOutlet]="paymentMethodsCard"></ng-container>
        </div>
        <div class="sub-col">
          <ng-container [ngTemplateOutlet]="restaurantInfoCard"></ng-container>
          <ng-container [ngTemplateOutlet]="nextInvoiceCard"></ng-container>
        </div>
      </div>
    } @else if (activeTab === 'add-ons' && displayAddon) {
      <div class="ao-header">
        <p class="ao-title">Add-ons marketplace</p>
        <p class="ao-subtitle">
          Extend Fork &amp; Find with kitchen, front-of-house and growth services. Billed at the end
          of each month for the days you use them, on the same invoice as your plan.
        </p>
      </div>

      <div class="ao-grid">
        @for (addOn of addOnsMarketplace; track addOn.name) {
          <div class="ao-card" [class.ao-card--disabled]="addOn.buttonDisabled">
            <div class="ao-card-media" [style.background]="addOn.gradient">
              <i [class]="addOn.icon" [style.color]="addOn.iconColor"></i>
              @if (addOn.badge) {
                <span
                  class="ao-badge"
                  [class.ao-badge--active]="addOn.badge.type === 'active'"
                  [class.ao-badge--locked]="addOn.badge.type === 'locked'"
                  [class.ao-badge--scheduled]="
                    addOn.badge.type === 'scheduled' || addOn.badge.type === 'requested'
                  "
                  [class.ao-badge--on-hold]="addOn.badge.type === 'on-hold'"
                  [class.ao-badge--rejected]="addOn.badge.type === 'rejected'"
                >
                  @if (addOn.badge.type === 'locked') {
                    <i class="bx bx-lock-alt"></i>
                  } @else {
                    <span class="ao-badge-dot"></span>
                  }
                  {{ addOn.badge.label }}
                </span>
              }
            </div>

            <div class="ao-card-body">
              <div class="ao-tag-row">
                <span class="ao-tag">{{ addOn.category }}</span>
                @if (addOn.tagPrice !== undefined) {
                  <span class="ao-tag-price"
                    >{{ getCurrencySymbol(currency) }}{{ addOn.tagPrice.toFixed(2) }}</span
                  >
                }
              </div>

              <p class="ao-card-title">{{ addOn.name }}</p>
              <p class="ao-card-desc">{{ addOn.description }}</p>

              @if (addOn.lockedMessage) {
                <p class="ao-locked-message">{{ addOn.lockedMessage }}</p>
              } @else {
                <div class="ao-price-row">
                  <div>
                    <span class="ao-price"
                      >{{ getCurrencySymbol(currency) }}{{ addOn.price.toFixed(2) }}</span
                    >
                    <span class="ao-price-unit">{{ addOn.priceUnit }}</span>
                  </div>
                  @if (addOn.quantity !== undefined) {
                    <div class="ao-stepper">
                      <button
                        type="button"
                        class="ao-stepper-btn"
                        aria-label="Decrease quantity"
                        [disabled]="addOn.quantityLocked"
                        (click)="decrementAddOnQuantity(addOn)"
                      >
                        −
                      </button>
                      <span class="ao-stepper-count">{{ addOn.quantity }}</span>
                      <button
                        type="button"
                        class="ao-stepper-btn"
                        aria-label="Increase quantity"
                        [disabled]="addOn.quantityLocked"
                        (click)="incrementAddOnQuantity(addOn)"
                      >
                        +
                      </button>
                    </div>
                  }
                </div>
              }

              @if (addOn.requestReason && addOn.requestStatus !== 'REQUESTED') {
                <p class="ao-request-reason">{{ addOn.requestReason }}</p>
              }
              @if (addOn.pendingNote) {
                <p class="ao-hardware-hint">{{ addOn.pendingNote }}</p>
              }
              @if (addOnBillingHint(addOn); as hint) {
                <p class="ao-hardware-hint">{{ hint }}</p>
              }
              @if (addOn.requestStatus === 'REQUESTED' || addOn.requestStatus === 'ON_HOLD') {
                <button
                  type="button"
                  class="sub-btn sub-btn--outline sub-btn--block"
                  [disabled]="addOn.actionInProgress"
                  (click)="cancelHardwareRequest(addOn)"
                >
                  {{ addOn.isReturn ? 'Cancel return' : 'Cancel request' }}
                  @if (addOn.actionInProgress) {
                    <i class="bx bx-loader-alt bx-spin"></i>
                  }
                </button>
              } @else if (!addOn.isSubscribed) {
                <button
                  type="button"
                  class="sub-btn"
                  [class.sub-btn--dark]="addOn.buttonStyle === 'dark'"
                  [class.sub-btn--outline]="
                    addOn.buttonStyle === 'outline' && !addOn.buttonDisabled
                  "
                  [class.btn-danger]="addOn.buttonDisabled"
                  [class.sub-btn--block]="true"
                  [disabled]="addOn.buttonDisabled || addOn.actionInProgress"
                  (click)="addToPlan(addOn)"
                >
                  {{ addOn.buttonLabel }}
                  @if (addOn.actionInProgress) {
                    <i class="bx bx-loader-alt bx-spin"></i>
                  }
                </button>
              } @else {
                <div class="ao-btn-row">
                  @if (
                    addOn.quantity !== undefined && addOn.quantity !== addOn.subscribedQuantity
                  ) {
                    <button
                      type="button"
                      class="sub-btn sub-btn--dark"
                      [disabled]="addOn.actionInProgress"
                      (click)="updateAddonQuantity(addOn)"
                    >
                      {{ quantityButtonLabel(addOn) }}
                      @if (addOn.actionInProgress) {
                        <i class="bx bx-loader-alt bx-spin"></i>
                      }
                    </button>
                  }
                  <button
                    type="button"
                    class="sub-btn sub-btn--outline"
                    [disabled]="addOn.actionInProgress"
                    (click)="removeAddon(addOn)"
                  >
                    {{ removeButtonLabel(addOn) }}
                    @if (addOn.actionInProgress) {
                      <i class="bx bx-loader-alt bx-spin"></i>
                    }
                  </button>
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else if (activeTab === 'billing-history') {
      <div class="bh-card">
        <div class="bh-table-wrap">
          <table class="bh-table">
            <colgroup>
              <col style="width: 12%" />
              <col style="width: 13%" />
              <col style="width: 10%" />
              <col style="width: 30%" />
              <col style="width: 8%" />
              <col style="width: 10%" />
              <col style="width: 10%" />
            </colgroup>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Billing Period</th>
                <th>Invoice Date</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Status</th>
                <th class="bh-actions-head">Actions</th>
              </tr>
            </thead>
            @if (billingHistory.length > 0) {
              <tbody>
                @for (invoice of billingHistory; track invoice.invoiceNumber) {
                  <tr [id]="invoiceRowId(invoice)" [class.is-highlighted]="isHighlighted(invoice)">
                    <td>{{ invoice.invoiceNumber }}</td>
                    <td>{{ invoice.billingPeriod }}</td>
                    <td>{{ invoice.invoiceDate }}</td>
                    <td>
                      @if (invoice.items.length > 0) {
                        <div class="bh-items">
                          @for (item of invoice.items; track $index) {
                            <div class="bh-item-row">
                              <span
                                class="bh-item-type"
                                [class.bh-item-type--addon]="item.type === 'ADDON'"
                                >{{ item.type }}</span
                              >
                              <span class="bh-item-label">{{ item.label }}</span>
                            </div>
                          }
                        </div>
                      } @else {
                        —
                      }
                    </td>
                    <td>{{ getCurrencySymbol(currency) }}{{ invoice.amount.toFixed(2) }}</td>
                    <td>
                      <span
                        class="bh-status"
                        [class.bh-status--paid]="invoice.status === 'paid'"
                        [class.bh-status--draft]="invoice.status === 'draft'"
                        [class.bh-status--void]="invoice.status === 'void'"
                        [class.bh-status--uncollectible]="invoice.status === 'uncollectible'"
                        >{{ invoice.statusLabel }}</span
                      >
                    </td>
                    <td>
                      <div class="bh-actions">
                        <button
                          type="button"
                          class="bh-icon-btn"
                          [class.bh-icon-btn--accent]="isHighlighted(invoice)"
                          aria-label="View invoice"
                          [disabled]="!invoice.hostedUrl"
                          (click)="openInvoice(invoice)"
                        >
                          <i class="bx bx-show"></i>
                        </button>
                        <button
                          type="button"
                          class="bh-icon-btn"
                          aria-label="Download invoice"
                          [disabled]="!invoice.pdfUrl"
                          (click)="downloadInvoice(invoice)"
                        >
                          <i class="bx bx-download"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            } @else {
              <tbody>
                <tr>
                  <td class="text-center" colspan="7" rowspan="7">
                    Still now invoices not generated
                  </td>
                </tr>
              </tbody>
            }
          </table>
        </div>
      </div>
    } @else if (activeTab === 'payment-method') {
      <div class="sub-col">
        <ng-container [ngTemplateOutlet]="paymentMethodsCard"></ng-container>
      </div>
    }
  }
</div>
